import { TypeReference } from '../data/type-reference.js'
import { BoRepositoryInspector, BusinessObject } from './business-object.js'
import { StaticEntityProperty } from './static-entity-property.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { StaticEntityEntry } from './static-entity-entry.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { Parameter } from '../script/parameter.js'
import {
	BoSpecificDetailsQuery,
	BoSpecificDetails,
} from './bo-specific-details.js'
import type { NameAndType } from '../types.js'

export const StaticEntitySources = {
	static: { label: 'Static', executionContextName: null },
	dynamicServerAndClient: {
		label: 'Dynamic (running on server and client)',
		executionContextName: 'serverAndClient',
	},
	dynamicServer: {
		label: 'Dynamic (needs to run on server)',
		executionContextName: 'server',
	},
} as const
export type StaticEntitySource = keyof typeof StaticEntitySources

@jsonClass('StaticEntity')
export class StaticEntity extends BusinessObject {
	@jsonProperty(String) source: StaticEntitySource = 'static'

	// @jsonProperty(String) idType: 'string' | 'number' = 'string'

	@jsonArrayProperty(StaticEntityProperty) properties: StaticEntityProperty[] =
		[new StaticEntityProperty({ name: 'label', type: 'string' })]
	@jsonProperty(String) displayProperty: string = 'label'
	@jsonArrayProperty(StaticEntityEntry) entries: StaticEntityEntry[] = []
	@jsonProperty(Boolean) hasOtherEntries: boolean = false

	@jsonArrayProperty(Parameter) inputs: Parameter[] = []
	@jsonProperty(String) dynamicCode: string = ''
	/** 0=cache forever, -1=never cache */
	@jsonProperty(Number) dynamicClientCachingSeconds: number = 0
	@jsonProperty(Number) dynamicServerCachingSeconds: number = 0

	get idType() {
		return (this.properties.find((p) => p.name == 'id')?.type || 'string') as
			| 'string'
			| 'number'
	}
	readonly affectsDb: boolean = true

	constructor(init?: Partial<StaticEntity>) {
		super()
		assignCommonProperties(this, init)
	}

	initializeNewBo(repositoryInspector: BoRepositoryInspector): void {
		super.initializeNewBo(repositoryInspector)
		this.ensureDefaultProperties()
	}

	getTypeReferences() {
		const staticEntityRef = this.getBoReference()
		return [
			new TypeReference(staticEntityRef),
			new TypeReference(staticEntityRef, undefined, ['Id']),
		]
	}

	getClassNames(): string[] {
		const qualifiedName = this.getQualifiedName()
		return [qualifiedName, `${qualifiedName}Option`, `${qualifiedName}Entry`]
	}

	getIdTypeConstructor() {
		return this.idType == 'string' ? 'String' : 'Number'
	}

	getIdTypeInitializer() {
		return this.idType == 'string' ? `''` : '0'
	}

	getOtherEntries() {
		return this.entries.filter((e) => e.values.get('$isOther') == 'true')
	}

	ensureDefaultProperties() {
		if (!this.properties.find((p) => p.name == 'id')) {
			this.properties.splice(
				0,
				0,
				new StaticEntityProperty({
					name: 'id',
					type: 'string',
				}),
			)
		}
		if (
			!this.properties.find((p) => p.name == '$isOther') &&
			this.hasOtherEntries
		) {
			this.properties.splice(
				1,
				0,
				new StaticEntityProperty({
					name: '$isOther',
					type: 'boolean',
				}),
			)
		}
	}

	isPropertyDeletable(property: StaticEntityProperty) {
		if (property?.name == 'id') return false
		if (property?.name == '$isOther' && this.hasOtherEntries) return false
		return true
	}

	isTypeAllowedForProperty(property: StaticEntityProperty, type: string) {
		if (property?.name == 'id') return ['string', 'number'].includes(type)
		if (property?.name == '$isOther' && this.hasOtherEntries)
			return ['boolean'].includes(type)
		return true
	}

	getPropertyDefaultValue(propertyName: string) {
		if (propertyName == 'id') return this.getIdTypeInitializer()
		if (propertyName == '$isOther') return 'false'

		const property = this.properties.find((p) => p.name == propertyName)
		return property?.getDefaultValue()
	}

	getBoSpecificDetails(query?: BoSpecificDetailsQuery): BoSpecificDetails {
		return {
			...super.getBoSpecificDetails(query),
			allPropertyNames: [
				'$displayValue',
				...this.properties.map((p) => p.name),
			],
		}
	}

	getPropertiesForPropertyTree(): NameAndType[] {
		return this.properties
	}
}
